/**
 * Energy Tracker Main App Component
 * 
 * CRITICAL: AuthScreen uses lazy loading to prevent webpack tree-shaking.
 * Previous issue: webpack cache corruption caused LoginForm to disappear from production bundle.
 * Solution: Explicit lazy loading + Suspense ensures component is never tree-shaked.
 */

import React, { useState, useEffect, Suspense, lazy } from 'react';
import { isAuthenticated as checkAuthToken, clearToken, isTokenExpired } from './utils/auth';
import api from './utils/api';
// import BolletteUpload from './components/BolletteUpload';
// import BolletteList from './components/BolletteList';
import './App.css';
import Dashboard from './components/Dashboard';

// Lazy load AuthScreen to prevent aggressive tree-shaking in production builds
// Reference: https://github.com/ziomik/energy-tracker/issues/96
const AuthScreen = lazy(() => import('./components/AuthScreen'));
const RegisterScreen = lazy(() => import('./components/RegisterScreen'));

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(checkAuthToken());
  const [authChecked, setAuthChecked] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Proactive token expiration check (on mount + every 5 minutes)
  // Skip if already on /login to prevent redirect loop (issue #151)
  useEffect(() => {
    const onLoginPage = window.location.pathname === '/login';
    
    const checkExpiration = () => {
      const token = localStorage.getItem('authToken');
      // Only redirect if there IS a token and it's expired
      // No token = already logged out, no redirect needed
      if (token && isTokenExpired()) {
        console.warn('[AUTH] Token expired — auto-logout');
        clearToken();
        setIsAuthenticated(false);
        window.location.href = '/login?expired=true';
      }
    };
    
    // Don't run expiration check/redirect on the login page itself
    if (onLoginPage) {
      // If we're on login with a stale token, just clear it silently
      if (localStorage.getItem('authToken')) {
        console.log('[AUTH] On login page with stale token — clearing');
        clearToken();
      }
      return;
    }
    
    // Check on mount
    checkExpiration();
    
    // Check every 5 minutes
    const interval = setInterval(checkExpiration, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const checkAuthStatus = async () => {
    try {
      console.log('[AUTH] Checking authentication status...');
      
      // Quick check: if no token in localStorage, skip server call
      if (!checkAuthToken()) {
        console.log('[AUTH] No token in localStorage');
        setIsAuthenticated(false);
        setAuthChecked(true);
        return;
      }

      // Verify token with server (uses api.js interceptor for Authorization header)
      const response = await api.get('/auth/status');
      const data = response.data;
      
      console.log('[AUTH] Server response:', data);
      setIsAuthenticated(data.authenticated);
      console.log('[AUTH] State updated - authenticated:', data.authenticated);
    } catch (error) {
      console.error('[AUTH] Check error:', error);
      // Token invalid/expired - clear it
      clearToken();
      setIsAuthenticated(false);
    } finally {
      setAuthChecked(true);
      console.log('[AUTH] Check completed - authChecked=true');
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setShowRegister(false);
  };

  const handleRegisterSuccess = () => {
    // Dopo registrazione, torna al login
    setShowRegister(false);
  };

  const handleLogout = async () => {
    try {
      // Clear token from localStorage
      clearToken();
      
      // Optional: notify server (though token is client-side only)
      await api.post('/auth/logout').catch(() => {
        // Ignore server errors on logout - token already cleared
      });
      
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
      // Always clear local state even if server call fails
      clearToken();
      setIsAuthenticated(false);
    }
  };

  const handleUploadSuccess = () => {
    // Trigger refresh della lista
    setRefreshTrigger(prev => prev + 1);
  };

  // Show nothing until auth check completes
  if (!authChecked) {
    console.log('[RENDER] Showing loading screen - authChecked=false');
    return (
      <div className="app-loading">
        <p>Caricamento...</p>
      </div>
    );
  }

  // Show login or register form if not authenticated
  if (!isAuthenticated) {
    console.log('[RENDER] Showing auth screen - showRegister=', showRegister);
    
    if (showRegister) {
      return (
        <Suspense fallback={<div className="app-loading"><p>Caricamento registrazione...</p></div>}>
          <RegisterScreen 
            onRegisterSuccess={handleRegisterSuccess}
            onLoginSuccess={handleLoginSuccess}
            onBackToLogin={() => setShowRegister(false)}
          />
        </Suspense>
      );
    }
    
    return (
      <Suspense fallback={<div className="app-loading"><p>Caricamento login...</p></div>}>
        <AuthScreen 
          onLoginSuccess={handleLoginSuccess}
          onRegisterClick={() => setShowRegister(true)}
        />
      </Suspense>
    );
  }

  console.log('[RENDER] Showing Dashboard - isAuthenticated=true');

  // Show main app if authenticated
  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="header-title">
            <h1>⚡ Energy Tracker</h1>
            <p>Monitora e ottimizza i tuoi consumi energetici</p>
          </div>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>
      
      <main className="app-main">
        {/* <BolletteUpload onUploadSuccess={handleUploadSuccess} /> */}
        <Dashboard />
      </main>
      
      <footer className="app-footer">
        <p>
          API Status: <a href="/api/health" target="_blank" rel="noopener noreferrer">Check</a>
        </p>
      </footer>

      {/* Version footer for debug cache issues */}
      <div style={{
        position: 'fixed',
        bottom: 10,
        right: 10,
        fontSize: '11px',
        color: '#999',
        background: 'rgba(255, 255, 255, 0.85)',
        padding: '4px 8px',
        borderRadius: '3px',
        border: '1px solid #ddd',
        fontFamily: 'monospace',
        zIndex: 9999,
        userSelect: 'all',
        cursor: 'default'
      }}>
        v{process.env.REACT_APP_VERSION || '1.0.0'} • {process.env.REACT_APP_BUILD_TIME || 'dev'}
      </div>
    </div>
  );
}

export default App;
